/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'primereact/tooltip';
import withUnsavedChangesGuard from '../../utils/navigationGuard';
import SuspenseLoader from '../../components/SuspenseLoader/SuspenseLoader';
import Reset from '../../assets/icons/refresh.svg';
import './Sidebar.scss';
import RecentChanges from './RecentChanges';
import useFilterApi from '../../hooks/useFilterAPI';
import api, { apiWithRetry } from '../../utils/api';

interface SidebarProps {
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
  onGo?: (filters: any) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  collapsed = false,
  onCollapse,
  onGo
}) => {

  /* ================= STATE ================= */
  const [summaryView, setSummaryView] = useState(false);

  const [year, setYear] = useState<string | null>(null);
  const [customer, setCustomer] = useState<number | null>(null);
  const [category, setCategory] = useState<number | null>(null);
  const [brand, setBrand] = useState<number | null>(null);

  const [yearOptions, setYearOptions] = useState<any[]>([]);
  const [customerOptions, setCustomerOptions] = useState<any[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [brandOptions, setBrandOptions] = useState<any[]>([]);
  const [recentFilters, setRecentFilters] = useState<any>(null);
  const [lastRefreshDate, setLastRefreshDate] = useState<string | null>(null);

  const [activeButton, setActiveButton] =
    useState<'portfolio' | 'category'>('portfolio');

  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const isPortfolioSummary = summaryView && activeButton === 'portfolio';
  const isCategorySummary = summaryView && activeButton === 'category';
  const navigate = useNavigate();

  const emitFilterChange = () => {
    window.dispatchEvent(new CustomEvent('filters:changed'));
  };

  useEffect(() => {
    const handleFilterChange = () => {
      setRecentFilters(null);
    };

    window.addEventListener('filters:changed', handleFilterChange);

    return () => {
      window.removeEventListener('filters:changed', handleFilterChange);
    };
  }, []);

  useEffect(() => {
    const fetchLastProcessed = async () => {
      try {
        const res = await apiWithRetry(() =>
          api.get('/etl/last-processed')
        );

        if (res?.data?.success) {
          const rawDate = res.data?.data?.lastUpdated || null;

          // format → DD/MM/YYYY
          const formatted = rawDate && new Date(rawDate)
            .toLocaleDateString('en-GB');

          setLastRefreshDate(formatted);
        }
      } catch (err) {
        console.error('Last processed API error:', err);
      }
    };

    fetchLastProcessed();
  }, []);

  /* ================= FETCH YEARS ================= */

  useEffect(() => {
    const fetchYears = async () => {
      try {

        const res = await apiWithRetry(() =>
          api.get('/fec/filters/years')
        );

        if (res?.data?.success) {
          const years = res.data.data.map((y: any) =>
            y.year.toString()
          );

          setYearOptions(years);
          setYear(years[0]);
        }
      } catch (err) {
        console.error('Year API error:', err);
      }
    };

    fetchYears();
  }, []);

  /* ================= FETCH CUSTOMERS ================= */

  useFilterApi(
    year
      ? `/fec/filters/customers?year=${year}`
      : null,
    setCustomerOptions,
    () => {
      setCustomer(null);
      setCategory(null);
      setBrand(null);
      setCategoryOptions([]);
      setBrandOptions([]);
    },
  );

  /* ================= FETCH CATEGORIES ================= */

  useFilterApi(
    year && customer && !isPortfolioSummary
      ? `/fec/filters/categories?year=${year}&customerId=${customer}`
      : null,
    setCategoryOptions,
    () => {
      setCategory(null);
      setBrand(null);
      setBrandOptions([]);
    },
  );

  /* ================= FETCH BRANDS ================= */

  useFilterApi(
    year && customer && category && !summaryView
      ? `/fec/filters/brands?year=${year}&customerId=${customer}&categoryId=${category}`
      : null,
    setBrandOptions,
    () => {
      setBrand(null);
    },
  );

  /* ================= RESET ================= */

  const resetFilters = () => {
    setCustomer(null);
    setCategory(null);
    setBrand(null);
    setSummaryView(false);
    setActiveButton('portfolio');
  };

  const isGoDisabled = () => {
    if (!year) return true;

    if (!summaryView) {
      // Detailed view → all required
      return !(customer && category && brand);
    }

    // Summary view
    if (activeButton === 'portfolio') {
      return !customer;
    }

    if (activeButton === 'category') {
      return !(customer && category);
    }

    return true;
  };

  const getGoTooltip = () => {
    if (!year) return 'Please select year';

    if (!summaryView) {
      if (!customer) return 'Please select customer';
      if (!category) return 'Please select category';
      if (!brand) return 'Please select brand';
    } else {
      if (!customer) return 'Please select customer';

      if (activeButton === 'category' && !category) {
        return 'Please select category';
      }
    }

    return '';
  };

  return (
    <>
      <Tooltip target=".go-button-wrapper" position="top" />
      <div
        className={`custom-sidebar ${collapsed ? 'collapsed' : ''}`}
      >
        {/* COLLAPSE ARROW */}
        <div
          className={`sidebar-arrow ${collapsed ? 'collapsed' : ''}`}
          onClick={() => onCollapse?.(!collapsed)}
        >
          <span className="last-refresh">
            Last Refresh Date : {lastRefreshDate || '--'}
          </span>
          <div className='flex'>
            <span className="arrow arrow-1"></span>
            <span className="arrow arrow-2"></span>
          </div>
        </div>

        <div className="sidebar-content">

          {/* Toggle */}
          <div className="view-toggle flex align-items-center justify-content-between">
            <span className={summaryView ? 'inactive' : ''}>Detailed</span>
            <div className="custom-toggle-wrapper">
              <InputSwitch
                checked={summaryView}
                onChange={(e) => {
                  withUnsavedChangesGuard(() => {
                    const nextSummaryView = e.value!;

                    setSummaryView(nextSummaryView);

                    // Clear only fields disabled in the destination view.
                    if (nextSummaryView) {
                      // Summary view: Brand is disabled.
                      setBrand(null);

                      // Portfolio summary also disables Category.
                      if (activeButton === 'portfolio') {
                        setCategory(null);
                      }
                    }

                    setRecentFilters(null);
                    emitFilterChange();
                  });
                }}
              />
            </div>
            <span className={!summaryView ? 'inactive' : ''}>Summary</span>
          </div>

          {/* ================= FILTER SECTION ================= */}
          <div className="sidebar-section">

            <div
              className="section-header"
            >
              <span>Filters</span>
              <div className="flex gap-3">
                <img
                  src={Reset}   // your svg
                  alt="Reset"
                  className="section-header--icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    resetFilters();
                    emitFilterChange();
                  }}
                />
                <i className={`pi ${filtersExpanded ? 'pi-chevron-up' : 'pi-chevron-down'} section-header--icon`} onClick={() => setFiltersExpanded(!filtersExpanded)} />
              </div>
            </div>

            {filtersExpanded && (
              <div className="filter-section">

                {/* Year */}
                <div className="filter-group">
                  <label>Select Year</label>
                  <Dropdown
                    value={year}
                    options={yearOptions}
                    onChange={(e) => {
                      withUnsavedChangesGuard(() => {
                        setYear(e.value)
                        emitFilterChange();
                      });
                    }}
                    placeholder="Year"
                    className="dropdown-custom"
                    appendTo={document.body}
                  />
                </div>

                {/* Customer */}
                <div className="filter-group">
                  <label>Customer</label>
                  <Dropdown
                    value={customer}
                    options={customerOptions}
                    onChange={(e) => {
                      withUnsavedChangesGuard(() => {
                        setCustomer(e.value);
                        emitFilterChange();
                      })
                    }}
                    placeholder="Customer"
                    appendTo={document.body}
                    disabled={!year}
                    optionLabel="name"
                    optionValue="id"
                  />
                </div>

                {/* Category */}
                <div className="filter-group">
                  <label>Category</label>
                  <Dropdown
                    value={category}
                    options={categoryOptions}
                    onChange={(e) => {
                      withUnsavedChangesGuard(() => {
                        setCategory(e.value);
                        emitFilterChange();
                      })
                    }}
                    placeholder="Category"
                    appendTo={document.body}
                    optionLabel="categoryName"
                    optionValue="categoryId"
                    disabled={!customer || isPortfolioSummary}
                  />
                </div>

                {/* Brand */}
                <div className="filter-group">
                  <label>Brand</label>
                  <Dropdown
                    value={brand}
                    options={brandOptions}
                    onChange={(e) => {
                      withUnsavedChangesGuard(() => {
                        setBrand(e.value);
                        emitFilterChange();
                      })
                    }}
                    placeholder="Brand"
                    appendTo={document.body}
                    optionLabel="brandName"
                    optionValue="brandId"
                    disabled={!category || summaryView}
                  />
                </div>

                {summaryView && (
                  <div className="toggle-buttons">
                    <Button
                      label="Portfolio"
                      className={activeButton === 'portfolio' ? 'active-toggle' : ''}
                      onClick={() => {
                        setActiveButton('portfolio');
                        setCategory(null);
                        emitFilterChange();
                      }}
                    />
                    <Button
                      label="Category"
                      className={activeButton === 'category' ? 'active-toggle' : ''}
                      onClick={() => {
                        setActiveButton('category');
                        emitFilterChange();
                      }}
                    />
                  </div>
                )}
                <span
                  className="go-button-wrapper"
                  data-pr-tooltip={isGoDisabled() ? getGoTooltip() : ''}
                >
                  <Button
                    label="Go"
                    className="go-button"
                    onClick={() => {
                      withUnsavedChangesGuard(() => {
                        navigate('/home');

                        const selectedCustomer = customerOptions.find(c => c.id === customer);
                        const selectedCategory = categoryOptions.find(c => c.categoryId === category);
                        const selectedBrand = brandOptions.find(b => b.brandId === brand);

                        const payload = {
                          year: year ? Number(year) : null,

                          customer,
                          customerName: selectedCustomer?.name,

                          category,
                          categoryName: selectedCategory?.categoryName,

                          brand,
                          brandName: selectedBrand?.brandName,

                          isSummaryView: summaryView
                        }

                        onGo?.(payload);
                        if (!summaryView) {
                          setRecentFilters(payload);
                        }
                      });
                    }}
                    disabled={isGoDisabled()}
                    tooltipOptions={{ position: 'top' }}
                  />
                </span>
              </div>
            )}
          </div>

          {/* ================= AUDIT / RECENT CHANGES ================= */}
          {!summaryView && recentFilters && (
            <div>
              <RecentChanges filters={recentFilters} />
            </div>
          )}
        </div>
      </div >
    </>
  );
};

export default Sidebar;
